/**
 * Authentication service for the Student Immunization Portal
 * Handles OTP (SMS, WhatsApp, Email) and ID verification authentication flows
 */

import { post, get, API_ENDPOINTS, setAuthToken, setSessionId, clearAuthToken } from './api';
import {
  Guardian,
  OTPRequest,
  OTPVerification,
  AuthSession,
  IDVerificationResult,
  ContactMethod,
} from '../types';
import { toE164 } from '../utils/phoneUtils';

/**
 * OTP request response
 */
export interface OTPRequestResponse {
  success: boolean;
  message: string;
  expiresIn?: number; // seconds until OTP expires
  expires_in?: number;
  channel?: string;
  maskedContact?: string; // e.g., "+1 ***-***-1234" or "j***@example.com"
}

/**
 * OTP verification response
 */
export interface OTPVerifyResponse {
  success: boolean;
  session: AuthSession;
  guardian: Guardian;
  isNewUser: boolean;
  message?: string;
}

/**
 * ID verification request
 */
export interface IDVerificationRequest {
  documentImage: string; // base64 encoded image
  documentType: 'passport' | 'driver_license' | 'national_id' | 'visa';
  selfieImage?: string; // optional selfie for face matching
  preferredLanguage?: string;
}

/**
 * Request SMS OTP
 */
export const requestSMSOTP = async (
  phone: string,
  countryCode?: string
): Promise<OTPRequestResponse> => {
  const e164Phone = toE164(phone, countryCode);
  
  const response = await post<OTPRequestResponse>(API_ENDPOINTS.AUTH.REQUEST_OTP, {
    contact: e164Phone,
    channel: 'sms',
    phone: e164Phone,
    method: 'sms',
  });

  return response;
};

/**
 * Verify SMS OTP
 */
export const verifySMSOTP = async (
  phone: string,
  code: string,
  countryCode?: string
): Promise<OTPVerifyResponse> => {
  const e164Phone = toE164(phone, countryCode);

  const response = await post<OTPVerifyResponse>(API_ENDPOINTS.AUTH.VERIFY_OTP, {
    contact: e164Phone,
    channel: 'sms',
    code,
    phone: e164Phone,
    method: 'sms',
  });

  // Store auth credentials
  if (response.session) {
    setAuthToken(response.session.token);
    setSessionId(response.session.sessionId);
  }

  return response;
};

/**
 * Request WhatsApp OTP
 */
export const requestWhatsAppOTP = async (
  phone: string,
  countryCode?: string
): Promise<OTPRequestResponse> => {
  const e164Phone = toE164(phone, countryCode);

  const response = await post<OTPRequestResponse>(API_ENDPOINTS.AUTH.REQUEST_WHATSAPP_OTP, {
    contact: e164Phone,
    channel: 'whatsapp',
    phone: e164Phone,
  });

  return response;
};

/**
 * Verify WhatsApp OTP
 */
export const verifyWhatsAppOTP = async (
  phone: string,
  code: string,
  countryCode?: string
): Promise<OTPVerifyResponse> => {
  const e164Phone = toE164(phone, countryCode);

  const response = await post<OTPVerifyResponse>(API_ENDPOINTS.AUTH.VERIFY_WHATSAPP_OTP, {
    contact: e164Phone,
    channel: 'whatsapp',
    code,
    phone: e164Phone,
  });

  // Store auth credentials
  if (response.session) {
    setAuthToken(response.session.token);
    setSessionId(response.session.sessionId);
  }

  return response;
};

/**
 * Request Email OTP
 */
export const requestEmailOTP = async (
  email: string
): Promise<OTPRequestResponse> => {
  const normalizedEmail = email.toLowerCase().trim();
  const response = await post<OTPRequestResponse>(API_ENDPOINTS.AUTH.REQUEST_EMAIL_OTP, {
    contact: normalizedEmail,
    channel: 'email',
    email: normalizedEmail,
  });

  return response;
};

/**
 * Verify Email OTP
 */
export const verifyEmailOTP = async (
  email: string,
  code: string
): Promise<OTPVerifyResponse> => {
  const normalizedEmail = email.toLowerCase().trim();
  const response = await post<OTPVerifyResponse>(API_ENDPOINTS.AUTH.VERIFY_EMAIL_OTP, {
    contact: normalizedEmail,
    channel: 'email',
    code,
    email: normalizedEmail,
  });

  // Store auth credentials
  if (response.session) {
    setAuthToken(response.session.token);
    setSessionId(response.session.sessionId);
  }

  return response;
};

/**
 * Verify ID document (for users without phone/email)
 */
export const verifyIDDocument = async (
  request: IDVerificationRequest
): Promise<{ success: boolean; result: IDVerificationResult; session?: AuthSession }> => {
  // Check if running in kiosk mode (same logic as useKioskSecurity)
  const isKioskMode =
    new URLSearchParams(window.location.search).get('mode') === 'kiosk' ||
    import.meta.env.VITE_APP_MODE === 'kiosk';

  const response = await post<{
    success: boolean;
    result: IDVerificationResult;
    session?: AuthSession;
  }>(API_ENDPOINTS.AUTH.VERIFY_ID, {
    ...request,
    is_kiosk: isKioskMode
  });

  // Store auth credentials if verification successful
  if (response.session) {
    setAuthToken(response.session.token);
    setSessionId(response.session.sessionId);
  }

  return response;
};

/**
 * Get current session
 */
export const getCurrentSession = async (): Promise<{
  session: AuthSession | null;
  guardian: Guardian | null;
}> => {
  try {
    const response = await get<{
      session: AuthSession;
      guardian: Guardian;
    }>(API_ENDPOINTS.AUTH.SESSION);

    return response;
  } catch {
    return { session: null, guardian: null };
  }
};

/**
 * Logout and clear session
 */
export const logout = async (): Promise<void> => {
  try {
    await post(API_ENDPOINTS.AUTH.LOGOUT, {});
  } finally {
    clearAuthToken();
  }
};

/**
 * Clear local session (for kiosk mode timeout)
 */
export const clearLocalSession = (): void => {
  clearAuthToken();
  sessionStorage.clear();
  
  // Clear IndexedDB if used
  if (window.indexedDB) {
    window.indexedDB.databases().then(databases => {
      databases.forEach(db => {
        if (db.name) window.indexedDB.deleteDatabase(db.name);
      });
    });
  }
};

/**
 * Request new OTP (generic, based on contact method)
 */
export const requestOTP = async (
  contact: string,
  method: ContactMethod,
  countryCode?: string
): Promise<OTPRequestResponse> => {
  switch (method) {
    case 'sms':
      return requestSMSOTP(contact, countryCode);
    case 'whatsapp':
      return requestWhatsAppOTP(contact, countryCode);
    case 'email':
      return requestEmailOTP(contact);
    default:
      throw new Error(`Unsupported contact method: ${method}`);
  }
};

/**
 * Verify OTP (generic, based on contact method)
 */
export const verifyOTP = async (
  contact: string,
  code: string,
  method: ContactMethod,
  countryCode?: string
): Promise<OTPVerifyResponse> => {
  switch (method) {
    case 'sms':
      return verifySMSOTP(contact, code, countryCode);
    case 'whatsapp':
      return verifyWhatsAppOTP(contact, code, countryCode);
    case 'email':
      return verifyEmailOTP(contact, code);
    default:
      throw new Error(`Unsupported contact method: ${method}`);
  }
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const { session } = await getCurrentSession();
  return session !== null && !isSessionExpired(session);
};

/**
 * Check if session is expired
 */
export const isSessionExpired = (session: AuthSession): boolean => {
  const now = new Date();
  const expiresAt = new Date(session.expiresAt);
  return now >= expiresAt;
};

/**
 * Refresh session (extend expiration)
 */
export const refreshSession = async (): Promise<AuthSession | null> => {
  try {
    const response = await post<{ session: AuthSession }>(
      `${API_ENDPOINTS.AUTH.SESSION}/refresh`,
      {}
    );
    
    if (response.session) {
      setAuthToken(response.session.token);
      setSessionId(response.session.sessionId);
      return response.session;
    }
    
    return null;
  } catch {
    return null;
  }
};

/**
 * Add additional contact method to existing account
 */
export const addContactMethod = async (
  contact: string,
  method: ContactMethod,
  countryCode?: string
): Promise<OTPRequestResponse> => {
  const payload: Record<string, string> = { method };
  
  if (method === 'email') {
    payload.email = contact.toLowerCase().trim();
  } else {
    payload.phone = toE164(contact, countryCode);
  }

  const response = await post<OTPRequestResponse>('/guardians/contact/add', payload);
  return response;
};

/**
 * Verify additional contact method
 */
export const verifyContactMethod = async (
  contact: string,
  code: string,
  method: ContactMethod
): Promise<{ success: boolean }> => {
  const response = await post<{ success: boolean }>('/guardians/contact/verify', {
    contact,
    code,
    method,
  });
  return response;
};